import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, AlertCircle, CheckCircle, Activity, UserPlus } from 'lucide-react';
import { API_URL, API_KEY } from '../config';

const css = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&family=DM+Serif+Display:ital@0;1&display=swap');

  :root {
    --teal:#0EA5A0; --teal-d:#0B8A85; --teal-dim:rgba(14,165,160,0.10);
    --sky:#38BDF8; --navy:#0F2744;
    --bg:#F4F8FB; --white:#FFFFFF;
    --border:rgba(14,165,160,0.18); --border2:rgba(14,165,160,0.28);
    --text1:#0F2744; --text2:#476585; --text3:#7A9AB5;
    --err:#DC2626; --err-bg:rgba(220,38,38,0.07);
    --suc:#059669; --suc-bg:rgba(5,150,105,0.08);
    --shadow:0 4px 24px rgba(14,165,160,0.10),0 1px 4px rgba(15,39,68,0.08);
    --shadow-btn:0 4px 18px rgba(14,165,160,0.35);
    --radius:18px; --radius-sm:8px;
    --fd:'DM Serif Display',Georgia,serif; --fb:'DM Sans',system-ui,sans-serif;
    --transition:all 0.22s cubic-bezier(0.4,0,0.2,1);
  }

  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
  body{font-family:var(--fb);background:var(--bg);color:var(--text1);-webkit-font-smoothing:antialiased}

  .auth-root{
    height:100vh;
    overflow:hidden;
    display:grid;
    grid-template-columns:1fr 1fr;
    background:linear-gradient(135deg,#EBF5FB 0%,#F4F8FB 40%,#EDF7F4 100%);
    position:relative;
  }
  .auth-root::before{
    content:''; position:fixed; inset:0;
    background-image:radial-gradient(rgba(14,165,160,0.11) 1px,transparent 1px);
    background-size:28px 28px; pointer-events:none; z-index:0;
  }

  .panel-left{
    position:relative; z-index:1; padding:56px 48px;
    display:flex; flex-direction:column; justify-content:center;
    border-right:1px solid var(--border);
    background:linear-gradient(160deg,rgba(14,165,160,0.07) 0%,transparent 60%);
    height:100%;
    overflow-y:auto;
  }
  .logo-wrap{margin-bottom:48px}
  .logo-wrap img{height:38px;width:auto;display:block}
  .logo-fallback{display:flex;align-items:center;gap:10px}
  .logo-icon{width:38px;height:38px;border-radius:10px;background:var(--teal);display:flex;align-items:center;justify-content:center}
  .logo-name{font-family:var(--fd);font-size:1.2rem;color:var(--navy)}

  .panel-tagline{font-family:var(--fd);font-size:clamp(1.75rem,2.8vw,2.4rem);line-height:1.2;color:var(--navy);margin-bottom:16px}
  .panel-tagline em{font-style:italic;color:var(--teal)}
  .panel-sub{font-size:.9rem;line-height:1.75;color:var(--text2);max-width:340px;margin-bottom:40px}

  .step-list{display:flex;flex-direction:column;gap:0}
  .step-item{display:flex;align-items:flex-start;gap:13px;position:relative}
  .step-item:not(:last-child)::after{content:'';position:absolute;left:14px;top:30px;width:1px;height:24px;background:var(--border2)}
  .step-num{width:29px;height:29px;flex-shrink:0;border-radius:50%;background:#fff;border:1.5px solid var(--teal);display:flex;align-items:center;justify-content:center;font-size:.72rem;font-weight:700;color:var(--teal)}
  .step-body{padding-bottom:24px}
  .step-t{font-size:.84rem;font-weight:600;color:var(--navy);margin-bottom:2px}
  .step-d{font-size:.76rem;color:var(--text2)}

  .panel-right{
    position:relative; z-index:1;
    display:flex; align-items:center; justify-content:center;
    padding:40px 28px;
    height:100%;
    overflow-y:auto;
  }
  .form-card{
    background:var(--white); border:1px solid var(--border);
    border-radius:var(--radius); padding:40px 36px;
    width:100%; max-width:420px; box-shadow:var(--shadow);
    animation:slideUp .5s cubic-bezier(0.4,0,0.2,1) both;
    margin:auto;
  }
  @keyframes slideUp{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}}

  .mobile-logo{display:none;margin-bottom:24px}
  .mobile-logo img{height:28px;width:auto}

  .form-title{font-family:var(--fd);font-size:1.6rem;color:var(--navy);margin-bottom:6px}
  .form-subtitle{font-size:.84rem;color:var(--text2);margin-bottom:24px}

  .prog-label{font-size:.69rem;font-weight:600;letter-spacing:.07em;text-transform:uppercase;color:var(--text3);margin-bottom:6px}
  .prog-track{height:4px;border-radius:3px;background:#E2EDF5;overflow:hidden;margin-bottom:20px}
  .prog-fill{height:100%;border-radius:3px;background:linear-gradient(90deg,var(--teal),var(--sky));transition:width .4s}

  .alert{display:flex;align-items:flex-start;gap:10px;padding:12px 15px;border-radius:var(--radius-sm);font-size:.8125rem;margin-bottom:18px;line-height:1.5}
  .alert-error{background:var(--err-bg);border:1px solid rgba(220,38,38,0.2);color:var(--err)}
  .alert-success{background:var(--suc-bg);border:1px solid rgba(5,150,105,0.2);color:var(--suc)}

  .field-row{display:grid;grid-template-columns:1fr 1fr;gap:12px}
  .field{margin-bottom:14px}
  .field-label{display:block;font-size:.69rem;font-weight:600;letter-spacing:.07em;text-transform:uppercase;color:var(--text3);margin-bottom:6px}
  .field-wrap{position:relative}
  .field-input{width:100%;background:#F8FAFC;border:1px solid #D4E4EF;border-radius:var(--radius-sm);padding:11px 13px;font-family:var(--fb);font-size:.9rem;color:var(--text1);outline:none;transition:var(--transition);appearance:none}
  .field-input::placeholder{color:#A8C0D0}
  .field-input:focus{border-color:var(--teal);background:#fff;box-shadow:0 0 0 3px rgba(14,165,160,0.14)}
  .field-input.has-icon{padding-right:44px}
  .field-icon-btn{position:absolute;right:12px;top:50%;transform:translateY(-50%);background:none;border:none;color:var(--text3);cursor:pointer;display:flex;align-items:center;padding:2px;transition:color .15s}
  .field-icon-btn:hover{color:var(--teal)}

  .str-bar{display:flex;gap:3px;margin-top:6px}
  .str-seg{flex:1;height:3px;border-radius:2px;background:#E2EDF5;transition:background .3s}
  .str-seg.weak{background:var(--err)}
  .str-seg.fair{background:#D97706}
  .str-seg.strong{background:var(--suc)}
  .req-list{display:flex;flex-direction:column;gap:4px;margin-top:8px}
  .req-item{display:flex;align-items:center;gap:6px;font-size:.72rem;color:var(--text3);transition:color .2s}
  .req-item.met{color:var(--suc)}
  .req-dot{width:5px;height:5px;border-radius:50%;background:#D4E4EF;flex-shrink:0;transition:background .2s}
  .req-item.met .req-dot{background:var(--suc)}

  .btn-primary{display:flex;align-items:center;justify-content:center;gap:8px;width:100%;padding:13px;background:var(--teal);border:none;border-radius:var(--radius-sm);font-family:var(--fb);font-size:.9375rem;font-weight:600;color:#fff;cursor:pointer;box-shadow:var(--shadow-btn);transition:var(--transition);letter-spacing:.01em;margin-top:4px}
  .btn-primary:hover:not(:disabled){background:var(--teal-d);transform:translateY(-1px);box-shadow:0 6px 24px rgba(14,165,160,.45)}
  .btn-primary:disabled{opacity:.6;cursor:not-allowed}

  .divider{display:flex;align-items:center;gap:12px;margin:20px 0;color:var(--text3);font-size:.72rem}
  .divider::before,.divider::after{content:'';flex:1;height:1px;background:#E2EDF5}
  .footer-text{text-align:center;font-size:.84rem;color:var(--text2)}
  .btn-link{background:none;border:none;color:var(--teal);cursor:pointer;font-size:inherit;font-family:inherit;padding:0;font-weight:500}

  .spinner{display:inline-block;width:18px;height:18px;border:2px solid rgba(255,255,255,.4);border-top-color:#fff;border-radius:50%;animation:spin .7s linear infinite}
  @keyframes spin{to{transform:rotate(360deg)}}

  @media(max-width:860px){
    .auth-root{
      grid-template-columns:1fr;
      height:auto;
      overflow:visible;
      min-height:100vh;
    }
    .panel-left{display:none}
    .panel-right{
      padding:40px 20px;
      height:auto;
      overflow:visible;
      min-height:100vh;
    }
    .mobile-logo{display:block}
    .field-row{grid-template-columns:1fr}
    .form-card{padding:32px 24px;margin:0}
  }
`;

function getStrength(pw) {
  let s = 0;
  if (pw.length >= 8) s++;
  if (/[A-Z]/.test(pw)) s++;
  if (/[0-9]/.test(pw)) s++;
  if (/[^A-Za-z0-9]/.test(pw)) s++;
  return s;
}

export default function SignupScreen() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', username: '', email: '', password: '', confirmPassword: '' });
  const [showPw, setShowPw] = useState(false);
  const [showCfm, setShowCfm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Reliable navigation after success — cleans up if component unmounts early
  useEffect(() => {
    if (!success) return;
    const timer = setTimeout(() => navigate('/login'), 2500);
    return () => clearTimeout(timer);
  }, [success, navigate]);

  const setField = (field) => (e) => {
    let val = e.target.value;
    if (field === 'name') val = val.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
    setForm(f => ({ ...f, [field]: val }));
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setError('');
    const { name, username, email, password, confirmPassword } = form;
    if (!name || !username || !email || !password || !confirmPassword) { setError('All fields are required.'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setError('Please enter a valid email address.'); return; }
    if (password !== confirmPassword) { setError('Passwords do not match.'); return; }
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Cytometrix-API-KEY': API_KEY },
        body: JSON.stringify({ email, username, name, password }),
      });
      const data = await res.json();
      if (res.ok) {
        setSuccess(true); // useEffect above handles the navigate
      } else {
        setError(data.message || 'Could not create account.');
      }
    } catch {
      setError('Cannot connect to server.');
    } finally {
      setLoading(false);
    }
  };

  const pw = form.password;
  const strength = getStrength(pw);
  const strengthCls = strength <= 1 ? 'weak' : strength <= 2 ? 'fair' : 'strong';
  const reqs = [
    { label: 'At least 8 characters', met: pw.length >= 8 },
    { label: 'One uppercase letter', met: /[A-Z]/.test(pw) },
    { label: 'One number', met: /[0-9]/.test(pw) },
    { label: 'One special character', met: /[^A-Za-z0-9]/.test(pw) },
  ];
  const filled = Object.values(form).filter(v => v.length > 0).length;
  const progress = Math.round((filled / 5) * 100);

  return (
    <>
      <style>{css}</style>
      <div className="auth-root">
        <div className="panel-left">
          <div className="logo-wrap">
            <img src="/logo.png" alt="Cytometrix" onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
            <div className="logo-fallback" style={{ display: 'none' }}>
              <div className="logo-icon"><Activity size={20} color="#fff" strokeWidth={2.5} /></div>
              <span className="logo-name">Cytometrix</span>
            </div>
          </div>
          <h1 className="panel-tagline">Join the<br /><em>Clinical Network</em></h1>
          <p className="panel-sub">Create your account to access AI-driven wound analysis tools designed for modern clinical workflows.</p>
          <div className="step-list">
            {[
              { t: 'Create your account', d: 'Fill in your professional details' },
              { t: 'Verify your email', d: 'Confirm your clinical email address' },
              { t: 'Access the platform', d: 'Start analyzing wound imagery' },
            ].map((s, i) => (
              <div key={s.t} className="step-item">
                <div className="step-num">{i + 1}</div>
                <div className="step-body"><p className="step-t">{s.t}</p><p className="step-d">{s.d}</p></div>
              </div>
            ))}
          </div>
        </div>

        <div className="panel-right">
          <div className="form-card">
            <div className="mobile-logo">
              <img src="/logo.png" alt="Cytometrix" onError={e => e.target.style.display = 'none'} />
            </div>
            <h2 className="form-title">Create Account</h2>
            <p className="form-subtitle">Register as a clinician or care provider</p>

            <p className="prog-label">Profile completion — {progress}%</p>
            <div className="prog-track"><div className="prog-fill" style={{ width: `${progress}%` }} /></div>

            {success && (
              <div className="alert alert-success">
                <CheckCircle size={15} style={{ flexShrink: 0, marginTop: 1 }} />
                Account created! Redirecting to sign in…
              </div>
            )}
            {error && (
              <div className="alert alert-error">
                <AlertCircle size={15} style={{ flexShrink: 0, marginTop: 1 }} />
                {error}
              </div>
            )}

            <form onSubmit={handleSignup}>
              <div className="field-row">
                <div className="field">
                  <label className="field-label">Full Name</label>
                  <input className="field-input" type="text" placeholder="Dr. Jane Smith" value={form.name} onChange={setField('name')} />
                </div>
                <div className="field">
                  <label className="field-label">Username</label>
                  <input className="field-input" type="text" placeholder="jsmith" value={form.username} onChange={setField('username')} />
                </div>
              </div>
              <div className="field">
                <label className="field-label">Email Address</label>
                <input className="field-input" type="email" placeholder="jane@hospital.ca" value={form.email} onChange={setField('email')} />
              </div>
              <div className="field">
                <label className="field-label">Password</label>
                <div className="field-wrap">
                  <input className="field-input has-icon" type={showPw ? 'text' : 'password'} placeholder="Min. 8 characters" value={form.password} onChange={setField('password')} />
                  <button type="button" className="field-icon-btn" onClick={() => setShowPw(!showPw)} tabIndex={-1}>
                    {showPw ? <EyeOff size={17} /> : <Eye size={17} />}
                  </button>
                </div>
                {pw && (
                  <>
                    <div className="str-bar">
                      {[1, 2, 3, 4].map(i => <div key={i} className={`str-seg ${i <= strength ? strengthCls : ''}`} />)}
                    </div>
                    <div className="req-list">
                      {reqs.map(r => (
                        <div key={r.label} className={`req-item ${r.met ? 'met' : ''}`}>
                          <span className="req-dot" />{r.label}
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
              <div className="field">
                <label className="field-label">Confirm Password</label>
                <div className="field-wrap">
                  <input className="field-input has-icon" type={showCfm ? 'text' : 'password'} placeholder="Repeat your password" value={form.confirmPassword} onChange={setField('confirmPassword')} />
                  <button type="button" className="field-icon-btn" onClick={() => setShowCfm(!showCfm)} tabIndex={-1}>
                    {showCfm ? <EyeOff size={17} /> : <Eye size={17} />}
                  </button>
                </div>
                {form.confirmPassword && form.password !== form.confirmPassword && (
                  <p style={{ fontSize: '.72rem', color: 'var(--err)', marginTop: 5 }}>Passwords do not match</p>
                )}
                {form.confirmPassword && form.password === form.confirmPassword && (
                  <p style={{ fontSize: '.72rem', color: 'var(--suc)', marginTop: 5 }}>Passwords match ✓</p>
                )}
              </div>
              <button type="submit" className="btn-primary" disabled={loading || success}>
                {loading ? <span className="spinner" /> : <><UserPlus size={17} />Create Account</>}
              </button>
            </form>
            <div className="divider">or</div>
            <p className="footer-text">Already have an account?{' '}<button className="btn-link" onClick={() => navigate('/login')}>Sign in</button></p>
          </div>
        </div>
      </div>
    </>
  );
}
